import type { SelectOption } from '@devexpress/dx-react-scheduler';
import type { AppointmentForm } from '@devexpress/dx-react-scheduler-material-ui';
import { useTheme } from '@mui/material/styles';
import dayjs from 'dayjs';
import React, { useCallback, useMemo, useState } from 'react';

import RadioButtonNode from '@components/RadioButtons/RadioButtonNode';
import RadioButtons from '@components/RadioButtons/RadioButtons';

import { WEEK_NUMBER_LABELS, DAYS_IN_WEEK } from '@constants/date';

import {
  changeRecurrenceOptions,
  getDaysOfWeekArray,
  getDaysOfWeekDates,
  getRadioGroupDisplayData,
  getRecurrenceOptions,
  handleStartDateChange,
  handleToDayOfWeekChange,
} from '@utils/routines';

import Label from '../Label';

const MonthlyRadioGroupEditorBase = ({
  getMessage,
  textEditorComponent: TextEditor,
  selectComponent: Select,
  appointmentData,
  onFieldChange,
  firstDayOfWeek,
}: AppointmentForm.RadioGroupProps) => {
  const [dayNumber, setDayNumber] = useState(
    new Date(appointmentData.startDate).getDate()
  );
  const [stateWeekNumber, setStateWeekNumber] = useState(
    Math.trunc((new Date(appointmentData.startDate).getDate() - 1) / 7)
  );
  const [stateDayOfWeek, setStateDayOfWeek] = useState(
    new Date(appointmentData.startDate).getDay()
  );

  const { rRule } = appointmentData;
  const recurrenceOptions = useMemo(
    () => (rRule ? getRecurrenceOptions(rRule) : {}),
    [rRule]
  );

  const { palette } = useTheme();

  const changeByMonthDay = useCallback(
    (nextByMonthDay: number | string) => {
      onFieldChange({
        rRule: handleStartDateChange(
          typeof nextByMonthDay === 'number'
            ? nextByMonthDay
            : parseInt(nextByMonthDay, 10),
          recurrenceOptions
        ),
      });
    },
    [onFieldChange, recurrenceOptions]
  );

  const {
    dayOfWeek,
    weekNumber,
    dayNumberTextField,
    radioGroupValue: value,
  } = getRadioGroupDisplayData(
    recurrenceOptions,
    stateDayOfWeek,
    stateWeekNumber,
    dayNumber,
    'onDayNumber',
    'onDayOfWeek'
  );

  const changeWeekNumber = useCallback(
    (nextWeekNumber: number | string) => {
      if (dayOfWeek !== undefined) {
        onFieldChange({
          rRule: handleToDayOfWeekChange(
            typeof nextWeekNumber === 'number'
              ? nextWeekNumber
              : parseInt(nextWeekNumber, 10),
            dayOfWeek,
            recurrenceOptions
          ),
        });
      }
    },
    [recurrenceOptions, onFieldChange, dayOfWeek]
  );
  const weekNumbers = useMemo(() => {
    const weekNums: SelectOption[] = [];
    WEEK_NUMBER_LABELS.forEach((weekNum, index) => {
      if (getMessage) {
        weekNums.push({
          text: getMessage(weekNum),
          id: index,
        });
      }
    });
    return weekNums;
  }, [getMessage]);

  const changeDayOfWeek = useCallback(
    (nextDayOfWeek: number | string) => {
      if (weekNumber !== undefined) {
        onFieldChange({
          rRule: handleToDayOfWeekChange(
            weekNumber,
            typeof nextDayOfWeek === 'number'
              ? nextDayOfWeek
              : parseInt(nextDayOfWeek, 10),
            recurrenceOptions
          ),
        });
      }
    },
    [onFieldChange, recurrenceOptions, weekNumber]
  );

  const daysOfWeek = useMemo(() => {
    const daysOfWeekArray = getDaysOfWeekArray(firstDayOfWeek);
    const daysOfWeekDates = getDaysOfWeekDates(firstDayOfWeek);
    return daysOfWeekDates.map((day, index) => ({
      text: dayjs(day).format('dddd'),
      id:
        index + 1 < DAYS_IN_WEEK
          ? daysOfWeekArray[index + 1]
          : daysOfWeekArray[0],
    }));
  }, [firstDayOfWeek]);

  const onRadioGroupValueChange = (
    newValue: boolean | number | string,
    oldValue: string | undefined
  ) => {
    if (newValue === oldValue) {
      return;
    }
    switch (newValue) {
      case 'onDayNumber':
        if (weekNumber !== undefined) {
          setStateWeekNumber(weekNumber);
          setStateDayOfWeek(dayOfWeek);
          onFieldChange({
            rRule: changeRecurrenceOptions({
              ...recurrenceOptions,
              bymonthday: dayNumber,
              byweekday: undefined,
            }),
          });
        }
        break;
      case 'onDayOfWeek':
        if (typeof recurrenceOptions.bymonthday === 'number') {
          setDayNumber(recurrenceOptions.bymonthday);
          onFieldChange({
            rRule: handleToDayOfWeekChange(
              stateWeekNumber,
              stateDayOfWeek,
              recurrenceOptions
            ),
          });
        }
        break;
      default:
        break;
    }
  };
  return (
    <RadioButtons
      alignment="vertical"
      onChange={(newValue) => onRadioGroupValueChange(newValue, value)}
      selected={value}
      style={{ width: '100%', marginBottom: 14, marginLeft: -5 }}
    >
      <RadioButtonNode style={{ width: '100%' }} value="onDayNumber">
        <div
          style={{
            display: 'inline-block',
            margin: 'auto 0',
            float: 'left',
          }}
        >
          <Label
            color={palette.text.secondary}
            text={getMessage ? getMessage('onLabel') : ''}
            mr={1}
          />
        </div>
        <TextEditor
          onValueChange={(value) => {
            changeByMonthDay(parseInt(value, 10));
          }}
          placeholder=""
          readOnly={false}
          type="numberEditor"
          value={dayNumberTextField ?? 1}
        />
        <div style={{ display: 'inline-block', margin: 'auto 0' }}>
          <Label
            color={palette.text.secondary}
            text={getMessage ? getMessage('ofEveryMonthLabel') : ''}
            ml={1}
          />
        </div>
      </RadioButtonNode>
      <RadioButtonNode style={{ width: '100%' }} value="onDayOfWeek">
        <div
          style={{
            display: 'inline-block',
            margin: 'auto 0',
            float: 'left',
          }}
        >
          <Label
            color={palette.text.secondary}
            text={
              getMessage
                ? `${getMessage('onLabel')} ${getMessage(
                    'theLabel'
                  ).toLowerCase()}`
                : ''
            }
            mr={1}
          />
        </div>
        <Select
          availableOptions={weekNumbers}
          onValueChange={changeWeekNumber}
          type="outlinedSelect"
          value={weekNumber?.toString() ?? ''}
        />
        <Select
          availableOptions={daysOfWeek}
          onValueChange={changeDayOfWeek}
          type="outlinedSelect"
          value={dayOfWeek?.toString() ?? ''}
        />
        <div style={{ display: 'inline-block', margin: 'auto 0' }}>
          <Label
            color={palette.text.secondary}
            text={getMessage ? getMessage('ofEveryMonthLabel') : ''}
          />
        </div>
      </RadioButtonNode>
    </RadioButtons>
  );
};

export default MonthlyRadioGroupEditorBase;
